import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { FileMetadataInfo } from '../../../models/uploaded-image-metadata.model';
import { GcpService } from '../../../services/gcp/gcp.service';
import { UploaderStorageService } from '../../../services/uploader-storage-service/uploader-storage.service';
import { State } from '../../../store/files/reducers/files.reducer';
import { selectGCPFile } from '../../../store/files/selectors/files.selectors';
import { PhotosValidationService } from './photos-validation.service';
import { XlifService } from './xlif.service';

/**
 * Validates photo metadata (coordinates) coming from XLIF / flat / GCP files.
 * Extracted from {@link UploaderUtilsService} to keep it lean.
 */
@Injectable({ providedIn: 'root' })
export class PhotoMetadataValidationService {
  constructor(
    private uploaderStorageService: UploaderStorageService,
    private photosValidatorService: PhotosValidationService,
    private store: Store,
    private gcpService: GcpService,
    private xlifService: XlifService,
  ) {}

  getGCPFileMetadata() {
    return new Promise<FileMetadataInfo[]>((resolve) => {
      this.store
        .select(selectGCPFile)
        .pipe(take(1))
        .subscribe(async (gcpFile) => {
          this.uploaderStorageService.invalidGCPFormat$.next(false);
          try {
            const gcpData = gcpFile ? await this.gcpService.getControlPointsFromFile(gcpFile) : [];
            resolve(gcpData);
          } catch (_) {
            this.uploaderStorageService.invalidGCPFormat$.next(true);
            resolve([]);
          }
        });
    });
  }

  validateMetadata(state: State) {
    const { flatFileName, controlPointsFileName: gcpFileName, csvFiles } = state;
    let { xlifData } = state;

    return new Promise<FileMetadataInfo[]>(async (resolve) => {
      if (flatFileName) {
        xlifData = await this.mergeFlatFileMetadata(xlifData, csvFiles, flatFileName);
      }

      const gcpData = gcpFileName ? await this.getGCPFileMetadata() : [];
      const gcpNames = gcpData.map((row) => row.name);

      const res = this.photosValidatorService.validatePhotos([...xlifData, ...gcpData]);
      const noCoords = res.noCoords.filter((n) => !gcpNames.includes(n));
      const wrongCoords = res.wrongCoords.filter((n) => !gcpNames.includes(n));
      const isGCPWrong = gcpNames.some((n) => res.wrongCoords.includes(n));
      const allWrongCoords =
        isGCPWrong && gcpFileName ? [gcpFileName, ...wrongCoords] : wrongCoords;

      this.uploaderStorageService.noCoordsFileNames$.next(noCoords);
      this.uploaderStorageService.wrongCoordsFileNames$.next(allWrongCoords);
      this.uploaderStorageService.validationWarnings$.next(res.warnings);

      this.uploaderStorageService.updateWrongMetadataInfo();
      resolve(xlifData);
    });
  }

  private async mergeFlatFileMetadata(
    xlifData: FileMetadataInfo[],
    csvFiles: File[],
    flatFileName: string,
  ): Promise<FileMetadataInfo[]> {
    const flatFile = csvFiles.find((file) => file.name === flatFileName) as File;
    const flatFileMetadata = await this.xlifService.getXlifDataFromFlatFile(flatFile);
    return xlifData.map((i) => {
      const dataFromFlatFile = flatFileMetadata.find((j) => j.name === i.name);
      return dataFromFlatFile || new FileMetadataInfo(i.name);
    });
  }
}
